import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Plus, Edit2, Trash2, X, Shield, User, Check, Heart } from 'lucide-react';
import { MODULOS } from '../components/layout/Layout';

// Cómo se muestra cada rol en las fichas. 'vsa' es el equipo de Vida, Salud y
// Ahorro: vive en /vida y no ve nada del sistema de la oficina.
const ROL = {
  admin: { label: 'Administrador',        color: 'var(--amber)',  dim: 'var(--amber-dim)',  Icon: Shield },
  vsa:   { label: 'Vida, Salud y Ahorro', color: 'var(--accent)', dim: 'var(--accent-dim)', Icon: Heart },
  // Los aliados (automotoras del convenio) se crean desde /socios, no desde
  // acá: ahí queda atado al socio. Esta entrada es para que se vean bien.
  socio: { label: 'Aliado comercial',     color: 'var(--accent)', dim: 'var(--accent-dim)', Icon: User },
  staff: { label: 'Administrativo',       color: 'var(--accent)', dim: 'var(--accent-dim)', Icon: User },
};
const rolDe = (u) => ROL[u.role] || ROL.staff;

// Los módulos habilitables para staff se definen en Layout (STAFF_MODULES) y se
// importan acá, así agregar un módulo al menú lo habilita solo en permisos.
const MODULOS_DEFAULT = ['clients', 'policies'];

function UserModal({ user, onClose }) {
  const { addUser, updateUser, currentUser } = useApp();
  const isEdit = !!user;
  const [form, setForm] = useState(user || { name: '', email: '', password: '', role: 'staff', modules: MODULOS_DEFAULT });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleModulo = (id) => {
    const actuales = form.modules || MODULOS_DEFAULT;
    const nuevos = actuales.includes(id) ? actuales.filter(m => m !== id) : [...actuales, id];
    set('modules', nuevos);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isEdit) await updateUser(user.id, form);
    else await addUser(form);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>{isEdit ? 'Editar usuario' : 'Nuevo usuario'}</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Nombre *</label><input value={form.name} onChange={e => set('name', e.target.value)} required /></div>
            <div className="form-group"><label className="form-label">Email *</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} required /></div>
            <div className="form-group"><label className="form-label">{isEdit ? 'Nueva contraseña (dejá en blanco para no cambiar)' : 'Contraseña *'}</label><input type="password" value={form.password} onChange={e => set('password', e.target.value)} required={!isEdit} /></div>
            <div className="form-group">
              <label className="form-label">Rol</label>
              <select value={form.role} onChange={e => set('role', e.target.value)}>
                <option value="staff">Administrativo — acceso a clientes y pólizas</option>
                <option value="admin">Administrador — acceso total + comisiones</option>
                <option value="vsa">Vida, Salud y Ahorro — módulo aparte</option>
              </select>
            </div>
            {form.role === 'admin' && (
              <div style={{ background: 'var(--amber-dim)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--amber)' }}>
                ⚠️ El rol Administrador tiene acceso total: todos los módulos, comisiones, CEO, actividad y configuración.
              </div>
            )}
            {form.role === 'vsa' && (
              <div style={{ background: 'var(--accent-dim)', border: '1px solid var(--accent)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--accent)' }}>
                💜 Este usuario entra únicamente al módulo <strong>/vida</strong>: ve los clientes que le derivamos,
                su seguimiento y sus pedidos a la oficina. <strong>No accede a nada del sistema</strong> —
                ni clientes, ni pólizas, ni renovaciones, ni siniestros, ni finanzas. Las pólizas que cierre
                sí quedan visibles para todo el equipo.
              </div>
            )}
            {form.role === 'staff' && (
              <div className="form-group">
                <label className="form-label">Módulos habilitados</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                  {MODULOS.map(m => {
                    const activo = (form.modules || MODULOS_DEFAULT).includes(m.id);
                    return (
                      <div key={m.id} onClick={() => toggleModulo(m.id)}
                        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', background: activo ? 'var(--accent-dim)' : 'var(--bg-input)', border: `1px solid ${activo ? 'var(--accent)' : 'var(--border)'}`, boxSizing: 'border-box', width: '100%' }}>
                        <span style={{ width: 18, height: 18, minWidth: 18, flexShrink: 0, borderRadius: 4, border: `2px solid ${activo ? 'var(--accent)' : 'var(--text-muted)'}`, background: activo ? 'var(--accent)' : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {activo && <Check size={12} color="#fff" strokeWidth={3} />}
                        </span>
                        <span style={{ textAlign: 'left' }}>{m.label}</span>
                      </div>
                    );
                  })}
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  El dashboard siempre está disponible. Los módulos de admin (comisiones, CEO, usuarios) nunca son accesibles para staff.
                </p>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn btn-primary">{isEdit ? 'Guardar' : 'Crear usuario'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const { users, deleteUser, currentUser } = useApp();
  const [modal, setModal] = useState(null);

  const confirmDelete = async (u) => {
    if (u.id === currentUser.id) return alert('No podés eliminar tu propio usuario.');
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try {
      const res = await deleteUser(u.id);
      if (res && res.needsConfirm) {
        // Tiene legajo de empleado vinculado: preguntamos qué hacer.
        const emp = res.linkedEmployee?.name || 'el empleado vinculado';
        const tambien = window.confirm(
          `${u.name} tiene un legajo de empleado vinculado (${emp}).\n\n` +
          `Aceptar = borrar también el empleado.\nCancelar = borrar solo el usuario (el legajo queda).`
        );
        await deleteUser(u.id, tambien); // cascade = tambien
      }
    } catch (e) {
      alert('Error al eliminar: ' + e.message);
    }
  };

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Usuarios</h1>
          <p className="page-subtitle">Gestión de accesos al sistema</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={15} /> Nuevo usuario</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {users.map(u => (
          <div key={u.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 42, height: 42, borderRadius: '50%',
                background: rolDe(u).dim,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-display)', fontWeight: 700,
                color: rolDe(u).color, fontSize: 16
              }}>
                {u.name.charAt(0)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontFamily: 'var(--font-display)' }}>{u.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
              </div>
              {u.id === currentUser.id && <span className="badge badge-blue" style={{ fontSize: 10 }}>Yo</span>}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {React.createElement(rolDe(u).Icon, { size: 13, color: rolDe(u).color })}
              <span style={{ fontSize: 12, color: rolDe(u).color, fontWeight: 600, fontFamily: 'var(--font-display)' }}>
                {rolDe(u).label}
              </span>
            </div>

            <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {u.role === 'admin' ? (
                <><div>✓ Clientes y pólizas</div><div>✓ Reportes</div><div>✓ Comisiones</div><div>✓ Gestión de usuarios</div></>
              ) : u.role === 'vsa' ? (
                <><div>✓ Su pipeline de Vida en /vida</div><div>✓ Pedidos a la oficina</div><div style={{ opacity: 0.4 }}>✗ Clientes y pólizas</div><div style={{ opacity: 0.4 }}>✗ Todo el resto del sistema</div></>
              ) : u.role === 'socio' ? (
                <><div>✓ Sus cotizaciones y comisiones en /socios</div><div style={{ opacity: 0.4 }}>✗ Datos de contacto de los clientes</div><div style={{ opacity: 0.4 }}>✗ Todo el resto del sistema</div></>
              ) : (
                <><div>✓ Clientes y pólizas</div><div>✓ Reportes</div><div style={{ opacity: 0.4 }}>✗ Comisiones</div><div style={{ opacity: 0.4 }}>✗ Usuarios</div></>
              )}
            </div>

            <div className="divider" style={{ margin: '0' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => setModal(u)}><Edit2 size={12} /> Editar</button>
              <button className="btn btn-danger btn-sm" onClick={() => confirmDelete(u)} disabled={u.id === currentUser.id}><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {modal && <UserModal user={modal === 'new' ? null : modal} onClose={() => setModal(null)} />}
    </div>
  );
}
